import React, { useState, useEffect } from "react";
import Layout from "antd/lib/layout";
import Menu from "antd/lib/menu";
import { Spin } from "antd";
import {
  UserOutlined,
  ScanOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";
import Header from "../Header/Header";
import { getWaitingRoomCache } from "../../utils/waitingRoomCache";
import { usePatientDataValidation } from "../../hooks/usePatientDataValidation";
import PatientDataRequired from "../PatientDataRequired";
import styles from "./menu.module.scss";

const { Sider, Content } = Layout;

interface WaitingRoomStatus {
  code: string;
  isPriority?: boolean;
  timestamp?: number;
}

const AppLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [waitingRoomStatus, setWaitingRoomStatus] =
    useState<WaitingRoomStatus | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Validar datos del paciente
  const { hasPatientData, isLoading, error, refetch } =
    usePatientDataValidation();

  // Check waiting room status on mount and when localStorage changes
  useEffect(() => {
    const checkWaitingRoomStatus = () => {
      const status = getWaitingRoomCache();
      setWaitingRoomStatus(status);
    };

    checkWaitingRoomStatus();

    // Listen for localStorage changes (in case of updates from other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "waitingRoomStatus") {
        checkWaitingRoomStatus();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Also check periodically in case cache expires
    const interval = setInterval(checkWaitingRoomStatus, 60000); // Check every minute

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Check if device is mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  // Si no tiene datos del paciente, mostrar vista bloqueante
  if (!hasPatientData && !isLoading) {
    return (
      <PatientDataRequired errorMessage={error || undefined} onRetry={refetch} />
    );
  }

  // Mostrar loading mientras se verifica
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <Spin size="large" tip="Verificando datos del paciente..." />
      </div>
    );
  }

  const menuItems = [
    {
      key: "/perfil",
      icon: <UserOutlined />,
      label: "Mi Perfil",
    },
    waitingRoomStatus
      ? {
          key: "/waiting-room/status",
          icon: <ClockCircleOutlined />,
          label: `En Cola: ${waitingRoomStatus.code}`,
          disabled: true,
        }
      : {
          key: "/waiting-room/join",
          icon: <ScanOutlined />,
          label: "Unirse a Sala de Espera",
        },
  ];

  const handleNavigate = (key: string) => {
    // Don't navigate if it's the disabled status item
    if (key !== "/waiting-room/status") {
      navigate(key);
    }
  };

  const BottomNavigation = () => (
    <div
      className={styles.bottomNavigation}
      style={{ display: isMobile ? "flex" : "none" }}
    >
      <div className={styles.bottomNavContainer}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.key;
          const IconComponent = item.icon;

          return (
            <button
              key={item.key}
              onClick={() => handleNavigate(item.key)}
              disabled={item.disabled}
              className={`${styles.bottomNavItem} ${
                isActive ? styles.active : item.disabled ? styles.disabled : ""
              }`}
            >
              <div className={styles.bottomNavIcon}>{IconComponent}</div>
              <span className={styles.bottomNavLabel}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <Layout
      style={{
        minHeight: "100dvh",
      }}
    >
      <Sider
        breakpoint="md"
        collapsedWidth={0}
        collapsible
        collapsed={collapsed || isMobile}
        onBreakpoint={(broken: boolean) => setCollapsed(broken)}
        onCollapse={(value: boolean) => setCollapsed(value)}
        trigger={null}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          background: "#ffffff",
          boxShadow: "2px 0 6px rgba(0,0,0,0.1)",
          display: isMobile ? "none" : "block",
        }}
      >
        <div
          style={{
            height: 60,
            margin: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0, 0, 0, 0.05)",
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{ height: "100%", width: "auto" }}
          />
        </div>
        <Menu
          theme="light"
          mode="inline"
          defaultSelectedKeys={["/perfil"]}
          onClick={({ key }) => handleNavigate(key)}
          items={menuItems.map((item) => ({
            key: item.key,
            icon: item.icon,
            label: item.label,
            disabled: item.disabled,
            style: item.disabled
              ? {
                  backgroundColor: "#f0f0f0",
                  color: "#52c41a",
                  fontWeight: "bold",
                }
              : {},
          }))}
        />
      </Sider>

      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 0 : 200,
          minHeight: "100vh",
        }}
      >
        <Header collapsed={collapsed} setCollapsed={setCollapsed} />
        <Content
          style={{
            margin: "16px",
            overflowY: "auto",
            height: "calc(100vh - 64px - 32px)",
            paddingBottom: isMobile ? "100px" : "0",
          }}
        >
          {children}
        </Content>
      </Layout>

      <BottomNavigation />
    </Layout>
  );
};

export default AppLayout;
